import _ from 'lodash';
import { GraphQLClient } from 'graphql-request';
import { buildPage } from 'page-builders/SalesPage';

import { FunnelPageType } from 'constant';

import {
  SalesPageDocument,
  SalesPagePathsDocument,
  type SalesPage,
  type SalesPageQuery,
  type PageTheme,
} from 'queries/graphql';
import { buildFunnelPath, buildFunnelPagePath } from 'utils/url';
import { decodeId } from 'utils/hashId';
import FetchError from 'utils/fetch-error';
import { getDefaultRevalidationTime } from 'utils/config';
import {
  type SalesFunnelData,
  buildGenericFunnelData,
  buildOfferConfig,
} from 'utils/funnel-data-context';

export async function getStaticProps(context) {
  const endpoint = process.env.HASURA_ENDPOINT;
  const gqlClient = new GraphQLClient(endpoint, {
    headers: {
      'x-hasura-admin-secret': process.env.HASURA_GRAPHQL_ADMIN_SECRET,
    },
  });

  try {
    const { params, preview = null } = context;

    const { path, funnelVariantId } = params;
    const decodedFid = _.isNaN(Number(funnelVariantId))
      ? decodeId(funnelVariantId)
      : funnelVariantId;

    const salesPageQuery = await gqlClient.request<SalesPageQuery>(SalesPageDocument, {
      path,
      funnelVariantId: decodedFid,
    });

    // @ts-ignore - The sales page object is every field in the sales page query, we don't actually need every field. Would be nice to just get the sales page object Type from the query...
    const pageData: SalesPage = salesPageQuery.salesPage?.[0];

    if (!pageData?.funnelVariant) {
      console.log('No pageData/funnelVariant found', {
        pageData,
        funnelVariant: pageData?.funnelVariant,
      });
      return { props: { preview, pageData: {}, funnelData: {} } };
    }

    // Theme can be null, so we need to provide a default value
    pageData.theme = pageData.theme || ({} as PageTheme);

    const checkoutPage = salesPageQuery.checkoutPages[0];

    const upsellPages =
      (pageData.funnelVariant?.upsellSteps || []).map(({ upsellPage }) =>
        salesPageQuery.upsellPages.find(({ id }) => id === upsellPage)
      ) || salesPageQuery.upsellPages;

    const firstUpsellPage = upsellPages?.[0];

    // Checkout page
    const checkoutPath = buildFunnelPath(
      buildFunnelPagePath({
        path: checkoutPage?.path,
        type: FunnelPageType.CHECKOUT_PAGE,
      }),
      funnelVariantId
    );

    // Upsell page
    const upsellUrl = firstUpsellPage?.path
      ? buildFunnelPath(
          buildFunnelPagePath({
            path: firstUpsellPage?.path,
            type: FunnelPageType.UPSELL_PAGE,
          }),
          funnelVariantId
        )
      : null;

    let merchantName = null;
    if (pageData.offers[0]) {
      // Use item brand name from the first sales page offer. Note: this would need to be
      // updated if we start using offers from different merchants on a sales page.
      // @lkuich - Does this still make any sense?
      merchantName = pageData.offers[0].offer.store.brand.name;
    }

    const offersConfig = pageData.offers
      .flatMap(pageOffer =>
        buildOfferConfig(
          pageOffer.offer,
          pageData.funnelVariant,
          FunnelPageType.SALES_PAGE,
          pageData.path
        )
      )
      .filter(Boolean);

    const funnelData: SalesFunnelData = {
      ...(await buildGenericFunnelData(
        pageData.funnelVariant,
        FunnelPageType.SALES_PAGE,
        pageData.path,
        pageData.theme
      )),
      upsellUrl,
      offersConfig,
      checkoutPath,
      merchantName,
    };

    return {
      revalidate: getDefaultRevalidationTime(),
      props: {
        preview,
        funnelData,
        pageData,
      },
    };
  } catch (err) {
    if (err instanceof FetchError) {
      const { statusCode } = err;

      switch (statusCode) {
        case 404: {
          return {
            redirect: {
              destination: '/',
              permanent: false,
            },
          };
        }

        default: {
          throw err;
        }
      }
    }

    throw err;
  }
}

export async function getStaticPaths() {
  const endpoint = process.env.HASURA_ENDPOINT;
  const gqlClient = new GraphQLClient(endpoint, {
    headers: {
      'x-hasura-admin-secret': process.env.HASURA_GRAPHQL_ADMIN_SECRET,
    },
  });

  const salesPagePathsQuery = await gqlClient.request<SalesPageQuery>(SalesPagePathsDocument);

  const pages = salesPagePathsQuery.salesPage as SalesPage[];

  const paths = pages.map(page => ({
    params: {
      funnelVariantId: page.funnelVariantId.toString(),
      path: page.path,
      // @lkuich - Have to see if these are actually used in storefront:
      slug: 'products',
      tag: 'salesPage',
    },
  }));

  return { paths, fallback: 'blocking' };
}

export default buildPage();
